import React from 'react';
import { observer } from 'mobx-react';
import {
  Alert,
  Badge,
  Card,
  Offcanvas,
  ProgressBar,
} from 'react-bootstrap';

import { GetStaffPortalStore } from 'SessionState/Staff/StaffPortalStore';

type JudgeDetailsCardProps = {
  judgeId: number | null;
  onClose: () => void;
};

const JudgeDetailsCard = observer(
  ({
    judgeId,
    onClose,
  }: JudgeDetailsCardProps) => {
    const staffStore =
      GetStaffPortalStore();

    const dashboard =
      staffStore.ReadOnlyDashboard;

    // =====================================================
    // SELECTED JUDGE
    // =====================================================

    const judge =
      dashboard?.judges?.find(
        (currentJudge) =>
          Number(
            currentJudge.eventJudgeId,
          ) === Number(judgeId),
      );

    const numericJudgeId =
      Number(
        judge?.eventJudgeId ?? 0,
      );

    // =====================================================
    // OVERALL PROGRESS
    // =====================================================

    const progress =
      Number(
        judge?.progressPercent ?? 0,
      );

    // =====================================================
    // LIVE STATUS
    // =====================================================

    const liveStatus =
      numericJudgeId
        ? staffStore
            .JudgeLiveStatuses[
            numericJudgeId
          ]
        : undefined;

    const isOnline =
      liveStatus?.isOnline === true ||
      liveStatus?.liveStatus ===
        'Online' ||
      liveStatus?.liveStatus ===
        'Scoring' ||
      liveStatus?.liveStatus ===
        'LoggedIn';

    // =====================================================
    // LAST COMPLETED CONTESTANT
    // =====================================================

    const lastCompleted =
      numericJudgeId
        ? staffStore
            .LastCompletedByJudge[
            numericJudgeId
          ]
        : undefined;

    const lastContestant =
      lastCompleted
        ? dashboard?.contestants?.find(
            (contestant) =>
              Number(
                contestant
                  .registeredContestantId,
              ) ===
              Number(
                lastCompleted
                  .registeredPageantContestantId,
              ),
          )
        : undefined;

    const lastAoc =
      lastCompleted
        ? judge?.areasOfCompetition
            ?.find(
              (aocProgress) =>
                Number(
                  aocProgress
                    .areaOfCompetition
                    ?.pageantAvailableEventId,
                ) ===
                Number(
                  lastCompleted
                    .pageantAvailableEventId,
                ),
            )
            ?.areaOfCompetition
        : undefined;

    // =====================================================
    // HELPERS
    // =====================================================

    const getProgressVariant = (
      value: number,
    ): string => {
      if (value >= 100) {
        return 'success';
      }

      if (value > 0) {
        return 'warning';
      }

      return 'secondary';
    };

    // =====================================================
    // UI
    // =====================================================

    return (
      <Offcanvas
        show={judgeId !== null}
        onHide={onClose}
        placement="end"
        backdrop
        scroll
        style={{
          width: 'min(500px, 100vw)',
        }}
      >
      <Offcanvas.Header
  closeButton
  className="border-bottom align-items-start p-4"
>
  {judge && (
    <div className="d-flex justify-content-between align-items-start w-100 me-3">
      <div>
        {/* ONLINE / OFFLINE */}

        <div className="mb-2">
          <Badge
            bg={
              isOnline
                ? 'success'
                : 'secondary'
            }
            className="text-uppercase"
          >
            {isOnline
              ? 'Online'
              : 'Offline'}
          </Badge>

          {judge.hasTestScore && (
            <Badge
              bg="danger"
              className="ms-2"
            >
              TEST COMPLETE
            </Badge>
          )}
        </div>

        {/* JUDGE */}

        <h3 className="mb-0">
          {judge.judgeLabel ||
            'Judge'}
        </h3>

        <div className="text-muted">
          {judge.judgeName ||
            'Judge Name Not Entered'}
        </div>

        {/* ACCESS CODE */}

        {judge.accessCodeLast4 && (
          <div className="small text-muted mt-1">
            Access Code:{' '}
            <span className="fw-semibold">
              {judge.accessCodeLast4}
            </span>
          </div>
        )}
      </div>

      {/* OVERALL PROGRESS */}

      <Badge
        bg={getProgressVariant(
          progress,
        )}
        className="fs-4 px-3 py-2"
      >
        {progress.toFixed(0)}%
      </Badge>
    </div>
  )}
</Offcanvas.Header>

<Offcanvas.Body>
          {!judge ? (
            <div className="text-center text-muted py-5">
              Judge not found.
            </div>
          ) : (
            <Card className="shadow-sm border-0">
              <Card.Body className="p-4">
                {/* ===================================== */}
                {/* LAST COMPLETED */}
                {/* ===================================== */}

                {lastCompleted &&
                  lastContestant &&
                  lastAoc && (
                    <Alert
                      variant="info"
                      className="py-2 px-3 mb-4"
                    >
                      <strong>
                        Complete:
                      </strong>{' '}
                      {lastAoc.aocName}
                      {' #'}
                      {
                        lastContestant
                          .contestantNumber
                      }{' '}
                      {
                        lastContestant
                          .contestantName
                      }
                    </Alert>
                  )}

                {/* ===================================== */}
                {/* AOC PROGRESS */}
                {/* ===================================== */}

                <div className="fw-bold text-uppercase small text-muted mb-3">
                  Areas of Competition
                </div>

                {judge
                  .areasOfCompetition
                  ?.map(
                    (aocProgress) => {
                      const aoc =
                        aocProgress
                          .areaOfCompetition;

                      if (!aoc) {
                        return null;
                      }

                      const aocPercent =
                        Number(
                          aocProgress
                            .progressPercent ??
                            0,
                        );

                      return (
                        <div
                          key={
                            aoc.pageantAvailableEventId
                          }
                          className="mb-4"
                        >
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong>
                              {
                                aoc.aocName
                              }
                            </strong>

                            <span className="text-muted">
                              {aocPercent.toFixed(
                                0,
                              )}
                              %
                            </span>
                          </div>

                          <ProgressBar
                            now={
                              aocPercent
                            }
                            variant={getProgressVariant(
                              aocPercent,
                            )}
                            style={{
                              height:
                                '10px',
                            }}
                          />

                          <div className="small text-muted mt-2">
                            {
                              aocProgress
                                .completedContestants ??
                              0
                            }{' '}
                            of{' '}
                            {
                              aocProgress
                                .totalContestants ??
                              0
                            }{' '}
                            contestants
                            complete
                          </div>
                        </div>
                      );
                    },
                  )}

                {!judge
                  .areasOfCompetition
                  ?.length && (
                  <div className="text-muted small">
                    No Areas of
                    Competition assigned
                    to this judge.
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    );
  },
);

export default JudgeDetailsCard;